import { useState } from 'react'
import type { FormEvent } from 'react'
import { Link, useSearch } from '@tanstack/react-router'
import { Eye, EyeOff } from 'lucide-react'
import { apiUrl } from '../../lib/api'
import { useT } from '../../i18n'
import { AuthShell } from './AuthShell'

const COPY = {
  en: { title: 'Choose a new password', description: 'Use at least 8 characters for your PEXIFY account.', password: 'New password', confirm: 'Confirm password', submit: 'Update password', loading: 'Updating…', short: 'Password must be at least 8 characters.', mismatch: 'Passwords do not match.', success: 'Your password has been updated. You can now sign in.', invalid: 'This reset link is invalid or has expired.', back: 'Back to sign in', newLink: 'Request a new link', show: 'Show password', hide: 'Hide password' },
  tr: { title: 'Yeni parola belirle', description: 'PEXIFY hesabın için en az 8 karakter kullan.', password: 'Yeni parola', confirm: 'Parolayı doğrula', submit: 'Parolayı güncelle', loading: 'Güncelleniyor…', short: 'Parola en az 8 karakter olmalı.', mismatch: 'Parolalar eşleşmiyor.', success: 'Parolan güncellendi. Artık giriş yapabilirsin.', invalid: 'Bu sıfırlama bağlantısı geçersiz veya süresi dolmuş.', back: 'Girişe dön', newLink: 'Yeni bağlantı iste', show: 'Parolayı göster', hide: 'Parolayı gizle' },
} as const

export function ResetPasswordPage() {
  const t = useT<Record<string, string>>(COPY)
  const { token } = useSearch({ strict: false }) as { token?: string }
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [status, setStatus] = useState<'idle' | 'success' | 'invalid'>('idle')
  const passwordTooShort = password.length > 0 && password.length < 8
  const passwordMismatch = confirm.length > 0 && password !== confirm
  const formValid = password.length >= 8 && password === confirm

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    if (!token || !formValid || loading) return
    setLoading(true)
    try {
      const response = await fetch(apiUrl('/auth/reset-password'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      })
      setStatus(response.ok ? 'success' : 'invalid')
    } catch {
      setStatus('invalid')
    } finally {
      setLoading(false)
    }
  }

  return (
    <AuthShell title={t.title} description={t.description}>
      {status === 'success' ? (
        <div className="pex-auth-form"><div className="pex-auth-feedback" data-tone="success" role="status">{t.success}</div><div className="pex-auth-row"><Link to="/login">← {t.back}</Link></div></div>
      ) : !token || status === 'invalid' ? (
        <div className="pex-auth-form"><div className="pex-auth-feedback" role="alert">{t.invalid}</div><div className="pex-auth-row"><Link to="/forgot-password">{t.newLink}</Link></div></div>
      ) : (
        <form onSubmit={handleSubmit} className="pex-auth-form" noValidate>
          <div className="pex-auth-field">
            <label htmlFor="new-password">{t.password}</label>
            <div className="pex-auth-input-wrap">
              <input id="new-password" type={showPassword ? 'text' : 'password'} autoComplete="new-password" value={password} onChange={(event) => setPassword(event.target.value)} minLength={8} required className="pex-auth-input" aria-describedby={passwordTooShort ? 'password-error' : undefined} />
              <button type="button" className="pex-auth-password-toggle" onClick={() => setShowPassword((value) => !value)} aria-label={showPassword ? t.hide : t.show}>{showPassword ? <EyeOff size={18} /> : <Eye size={18} />}</button>
            </div>
            {passwordTooShort && <span id="password-error" className="text-xs text-[var(--color-danger)]">{t.short}</span>}
          </div>
          <div className="pex-auth-field">
            <label htmlFor="confirm-password">{t.confirm}</label>
            <input id="confirm-password" type={showPassword ? 'text' : 'password'} autoComplete="new-password" value={confirm} onChange={(event) => setConfirm(event.target.value)} minLength={8} required className="pex-auth-input" aria-describedby={passwordMismatch ? 'confirm-error' : undefined} />
            {passwordMismatch && <span id="confirm-error" className="text-xs text-[var(--color-danger)]">{t.mismatch}</span>}
          </div>
          <button type="submit" disabled={loading || !formValid} className="pex-auth-submit">{loading ? t.loading : t.submit}</button>
        </form>
      )}
    </AuthShell>
  )
}
